import { useState, useCallback } from 'react';
import { apiService } from '../../../services/api';
import { PositionAnalysisResponse, PositionAnalysisRequest } from '../../../types';

/**
 * "Ask coach" for one position. Same request the position card sent (moved
 * here unchanged when the card became the ledger + cockpit, Oct 2026), so the
 * AI Coach backend sees an identical payload.
 */
export function usePositionCoach(position: any) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<PositionAnalysisResponse | null>(null);

  const ask = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setRecommendation(null);
    try {
      let valueHistory: any[] = [];
      try {
        const historyResponse = await apiService.getPositionValueHistory(position.id);
        valueHistory = historyResponse.snapshots || [];
      } catch {
        // value history is optional
      }
      const payload: PositionAnalysisRequest = {
        position_id: position.id,
        ticker: position.ticker,
        sentiment: position.sentiment || 'neutral',
        num_contracts: position.numContracts || 1,
        entry_date: position.entryDate,
        expiry_date: position.expiryDate,
        strike_price: position.strikePrice,
        entry_premium: position.entryPremium,
        entry_price: position.entryPrice,
        current_price: position.currentPrice,
        current_premium: position.currentPremium,
        current_delta: position.currentDelta,
        days_to_expiration: position.daysToExpiration,
        implied_volatility: position.impliedVolatility,
        // Gold v2 phase 3b: the position's gold figures (gold.v_position)
        stock_pnl: position.stockPnl,
        option_pnl: position.optionPnl,
        cc_pnl: position.totalPnl,
        value_history: valueHistory.length > 0 ? valueHistory.map((s: any) => ({
          snapshot_date: s.snapshot_date,
          total_pnl_cumulative: s.total_pnl_cumulative,
          stock_pnl_cumulative: s.stock_pnl_cumulative,
          option_pnl_cumulative: s.option_pnl_cumulative,
        })) : undefined,
        is_campaign: (position.legCount ?? 1) > 1,
        leg_count: position.legCount ?? 1,
        campaign_entry_date: position.campaignEntryDate,
        campaign_entry_price: position.stockEntryPrice,
        safety_floor: position.netBasis,
        campaign_total_pnl: position.campaignPnl,
        leg_pnl: position.optionPnl,
        campaign_id: position.campaignId,
        position_type: position.positionType,
        is_wheel: position.isWheel,
      };
      setRecommendation(await apiService.analyzePosition(payload));
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to analyze position');
    } finally {
      setLoading(false);
    }
  }, [position]);

  return { open, loading, error, recommendation, ask, close: () => setOpen(false) };
}
